'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import NextImage from 'next/image';
import { AiGeneratedBadge } from '@eliadmin/core/src/components/image/ai-generated-badge';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '../../ui/carousel-controls';
import { lazy as lazyProductLightbox, Suspense as ProductLightboxBoundary } from 'react';

const LightboxComponent = lazyProductLightbox(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);
// A hidden responsive gallery must not compete with the visible LCP image.
const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
import { useState, useRef, useEffect } from 'react';

export function ProductImages() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const options = {
    loop: true,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex, api: carouselApi } = carouselContext;

  const images = filerImages(
    templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false,
    product,
    selectedVariant,
  );

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) return;

    const container = thumbnailContainerRef.current;
    if (!container) return;

    const gap = 6;
    const thumbWidth = (container.clientWidth - 4 * gap) / 5;
    const scrollLeft =
      selectedIndex * (thumbWidth + gap) - container.clientWidth / 2 + thumbWidth / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [selectedIndex]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      {isOpen && (
        <ProductLightboxBoundary fallback={null}>
          <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
        </ProductLightboxBoundary>
      )}
      <div className="w-full max-w-[807px]">
        <div className="hidden w-full grid-cols-2 gap-4 md:grid">
          {images.map((image, index) => (
            <button
              type="button"
              aria-label={resolveImageAlt(image, product?.name)}
              key={index}
              className={cn(
                'relative aspect-square w-full cursor-pointer rounded-[4px] bg-[#F4F4F4] transition-all hover:opacity-90',
                index % 3 === 0 ? 'col-span-2' : 'col-span-1',
              )}
              onClick={handleOpenGallery(images, index)}
            >
              <picture>
                <source media="(max-width: 767px)" srcSet={EMPTY_IMAGE} />
                <NextImage
                  alt={resolveImageAlt(image, product?.name)}
                  src={image?.url ?? '/images/placeholder.png'}
                  fill
                  // Grid is md+ only, max 807px wide: every third image spans both columns.
                  sizes={
                    index % 3 === 0
                      ? '(min-width: 768px) 807px, 100vw'
                      : '(min-width: 768px) 400px, 50vw'
                  }
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : undefined}
                  className="absolute inset-0 z-0 h-full w-full object-contain object-center p-2 sm:p-3 lg:p-4"
                />
              </picture>
              {/[?&]ai=1(?:&|$)/.test(image?.url ?? '') && <AiGeneratedBadge />}
            </button>
          ))}
        </div>
        <div className="relative block w-full md:hidden">
          <CarouselControls
            className={{
              main: 'absolute left-1/2 top-1/2 z-20 flex w-[calc(100%_-_12px)] -translate-x-1/2 -translate-y-1/2 items-center justify-between',
              button:
                'sm max-h-8 min-h-8 min-w-8 max-w-8 rounded-[4px] bg-walnut !p-0 text-white hover:bg-walnut/90 sm:max-h-10 sm:min-h-10 sm:min-w-10 sm:max-w-10',
              next: 'h-4 w-4 text-white',
              prev: 'h-4 w-4 text-white',
            }}
          />
          <Carousel
            options={options}
            className={{
              container: 'w-full',
              main: 'w-full',
              wrapper: 'w-full',
              slide: 'w-full overflow-hidden rounded-[4px] bg-white',
            }}
            slides={images.map((image, index) => (
              <button
                type="button"
                key={index}
                className="relative block w-full"
                aria-label={resolveImageAlt(image, product?.name)}
                onClick={handleOpenGallery(images, index)}
              >
                <picture>
                  <source media="(min-width: 768px)" srcSet={EMPTY_IMAGE} />
                  <NextImage
                    key={index}
                    src={image?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(image, product?.name)}
                    height={0}
                    width={0}
                    className="aspect-square w-full bg-[#F4F4F4] object-contain object-center p-2 lg:p-3"
                    sizes="100vw"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : undefined}
                  />
                </picture>
                {/[?&]ai=1(?:&|$)/.test(image?.url ?? '') && <AiGeneratedBadge />}
              </button>
            ))}
          />
        </div>

        <div
          ref={thumbnailContainerRef}
          className="mt-[6px] flex w-full flex-row gap-[6px] overflow-x-auto [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden"
        >
          {images.map((image, index) => (
            <button
              type="button"
              key={index}
              aria-label={`${product?.name} ${index + 1}`}
              aria-pressed={selectedIndex === index}
              className="w-[calc((100%-24px)/5)] flex-shrink-0"
              onClick={() => carouselApi?.scrollTo(index)}
            >
              <Image
                key={index}
                src={image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(image, product?.name)}
                height={0}
                width={0}
                className={cn(
                  'aspect-square w-full cursor-pointer rounded-[4px] bg-[#F4F4F4] object-contain object-center p-1 opacity-50 transition-all lg:p-3',
                  selectedIndex === index && 'opacity-100',
                  selectedIndex !== index && 'hover:opacity-75',
                )}
                sizes="20vw"
                // Thumbnails select the slide; tapping the main slide opens the lightbox. The old
                // handler opened the lightbox before `imagesSorted` was ever set, so it was empty.
              />
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images =
    showOnlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
